<template>
	<ModalGenericModal
		:title="$t('termsAndConditions')"
		container-class="w-full md:w-3/4 lg:w-[45em] h-full md:h-[80vh] p-1 termsAndConditionsPopupComponent"
		:click-outside-enabled="false"
		:hide-close-btn="true"
		ref="baseModal">
		<div
			class="pb-2 h-[89vh] md:h-[72vh] bg-popup-bg border-x-2 border-b-2 border-popup-border rounded-b-lg termsAndConditionsBody">
			<div class="font-thin px-2 md:px-14 pt-4 text-left overflow-auto h-full">
				<div
					class="flex flex-col break-words termsContent"
					v-html="terms"></div>

				<LazyCheckoutSignature v-if="shouldShowSignature" />

				<div class="flex justify-evenly py-8">
					<ButtonModalClose
						:label="$t('decline')"
						class="w-32"
						@click="declined" />
					<ButtonModalAccept
						:label="$t('accept')"
						:disabled="shouldDisableAccept"
						class="w-32"
						@click="accepted" />
				</div>

				<!--		This captcha text must be displayed in the user flow		-->
				<div class="text-xxs text-center italic captcha">
					This site is protected by reCAPTCHA and the Google
					<a href="https://policies.google.com/privacy">Privacy Policy</a> and
					<a href="https://policies.google.com/terms">Terms of Service</a> apply.
				</div>
			</div>
		</div>
	</ModalGenericModal>
</template>

<script setup>
	import termsContent from "~/assets/static/termsAndConditions.html?raw"
	import { useReCaptcha } from "vue-recaptcha-v3"
	import { SHOW_ERROR } from "assets/events/errors.js"
	import { CART_CLEARED } from "assets/events/cart.js"

	const runtimeConfig = useRuntimeConfig()
	const { $eventBus } = useNuxtApp()
	const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()

	const shouldShowSignature = runtimeConfig.public.ENABLE_SIGNATURE_BOX

	const { termsOverride } = defineProps({
		termsOverride: {
			type: String,
			default: "",
		},
	})
	const terms = ref(!!termsOverride ? termsOverride : termsContent)

	const emits = defineEmits(["declined", "accepted"])
	const baseModal = ref(null)

	const termsStore = useTermsStore()
	const { signature } = storeToRefs(termsStore)
	const shouldDisableAccept = computed(() => {
		return shouldShowSignature && !signature.value
	})

	const openModal = () => baseModal.value?.openModal()

	const closeModal = () => baseModal.value?.closeModal()

	const declined = () => {
		closeModal()
		emits("declined")
	}

	const accepted = async () => {
		try {
			await recaptchaLoaded()
			const token = await executeRecaptcha("checkout")
			emits("accepted", { captchaToken: token })
		} catch (error) {
			$eventBus.$emit(SHOW_ERROR, {
				bodyText: `Verification failed. Please try again.`,
				closeCallback: reloadNuxtApp,
			})
		} finally {
			closeModal()
		}
	}

	onMounted(() => {
		//The event is triggered when the cart expires, good time to close the modal here.
		$eventBus.$on(CART_CLEARED, closeModal)
	})

	onUnmounted(() => {
		$eventBus.$off(CART_CLEARED, closeModal)
	})

	defineExpose({
		openModal,
	})
</script>

<style lang="scss" scoped></style>
